import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AgentCategory } from '../../types';
import { PricingModal } from '../Pricing';
import { Button } from '@/components/ui/button';
import { 
  UserCircleIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
  ArrowLeftStartOnRectangleIcon,
  ArrowTrendingUpIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import ThemeToggle from '../ThemeToggle';

interface NavbarProps {
  toggleSidebar: () => void;
  isSidebarOpen: boolean;
  agentName?: string;
  agentCategory?: AgentCategory;
}

const Navbar: React.FC<NavbarProps> = ({ toggleSidebar, isSidebarOpen, agentName, agentCategory }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Função para capitalizar apenas a primeira letra de cada palavra
  const capitalizeWords = (text: string): string => {
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const currentUser = {
    name: user?.firstName && user?.lastName 
      ? capitalizeWords(`${user.firstName} ${user.lastName}`)
      : user?.email || 'Usuário',
    email: user?.email || ''
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };
  
  // Placeholder handlers for new menu items
  const handleMyAccount = () => {
    navigate('/app/profile');
    setIsDropdownOpen(false);
  };
  
  const handleSubscription = () => {
    navigate('/app/profile?tab=subscription');
    setIsDropdownOpen(false);
  };

  const handleHelpCenter = () => {
    navigate('/app/support-messages');
    setIsDropdownOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <nav className="shadow-md h-14 md:h-16 flex items-center justify-between px-3 md:px-6 sticky top-0 z-20 backdrop-blur-md border-b border-[#E0D5C7]/20" style={{ backgroundColor: '#F3EDE3' }}>
      <div className="flex items-center">
        <button 
          onClick={toggleSidebar} 
          className="text-[#5D3A1A] hover:text-[#C4943B] focus:outline-none z-50"
          aria-label={isSidebarOpen ? "Fechar sidebar" : "Abrir sidebar"}
          aria-controls="sidebar"
          aria-expanded={isSidebarOpen}
        >
          <div className={`hamburger ${isSidebarOpen ? 'active' : ''}`} aria-hidden="true">
            {/* Hamburger spans will inherit background color from parent's text color via CSS variable */}
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        <div className="flex items-center ml-2 md:ml-4 font-sans flex-1 min-w-0">
            <button 
              type="button"
              onClick={toggleSidebar}
              className="flex items-center gap-2 text-xs md:text-sm text-[#5D3A1A] font-semibold hover:text-[#C4943B] transition-colors touch-manipulation whitespace-nowrap"
              aria-label="Toggle menu"
            >
              <Bars3Icon className="w-4 h-4 md:w-5 md:h-5" />
              MENU
            </button>
            
            {/* Breadcrumb - Desktop only */}
            {(agentCategory || agentName !== 'MENU') && (
              <div className="hidden md:flex items-center ml-2">
                {agentCategory && (
                    <>
                        <span className="text-sm text-[#5D3A1A]/70 font-medium">{agentCategory}</span>
                        <span className="mx-2 text-[#5D3A1A]/50">/</span>
                    </>
                )}
                {agentName !== 'MENU' && (
                  <span className="text-sm text-[#5D3A1A] font-semibold">{agentName}</span>
                )}
              </div>
            )}
        </div>

      </div>

      <div className="flex items-center space-x-2 md:space-x-4">
        {/* Welcome Message - Hidden on mobile */}
        <div className="hidden md:flex items-center gap-2">
          <span className="text-sm md:text-base text-[#5D3A1A]/70">Seja Bem Vindo!</span>
          <span className="text-xs md:text-sm font-semibold text-[#5D3A1A]">{currentUser.name}</span>
        </div>
        
        <div className="relative" ref={dropdownRef}>
          <button 
            onClick={toggleDropdown}
            className="flex items-center space-x-1 md:space-x-2 text-sm focus:outline-none touch-manipulation p-1.5 md:p-2 hover:bg-[#E0D5C7]/30 rounded-md transition-colors" 
            aria-label="Menu do usuário"
            aria-haspopup="true"
            aria-expanded={isDropdownOpen}
          >
            {/* User Avatar/Initial on mobile */}
            <div className="md:hidden w-8 h-8 rounded-full bg-[#C4943B] flex items-center justify-center text-white font-semibold text-xs">
              {currentUser.name.charAt(0).toUpperCase()}
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="hidden md:block h-5 w-5 text-[#5D3A1A]" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          
          {isDropdownOpen && (
            <div 
              className="absolute right-0 mt-2 w-56 md:w-60 origin-top-right rounded-md shadow-2xl ring-1 ring-[#E0D5C7] ring-opacity-20 focus:outline-none z-50 transition ease-out duration-100 transform opacity-100 scale-100"
              style={{ backgroundColor: '#F3EDE3' }}
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="user-menu-button"
            >
              <div className="px-4 py-3 border-b border-[#E0D5C7]">
                <p className="text-sm font-semibold text-[#5D3A1A] truncate" role="presentation">{currentUser.name}</p>
                <p className="text-xs text-[#5D3A1A]/70 truncate hidden md:block" role="presentation">{currentUser.email}</p>
              </div>
              
              <div className="py-1" role="none">
                <button
                  onClick={handleMyAccount}
                  className="w-full text-left flex items-center px-4 py-3 text-sm text-[#5D3A1A] hover:bg-[#E0D5C7]/30 hover:text-[#C4943B] transition-colors duration-150 touch-manipulation"
                  role="menuitem"
                >
                  <UserCircleIcon className="w-5 h-5 mr-3" aria-hidden="true" />
                  Minha Conta
                </button>
                <button
                  onClick={handleSubscription}
                  className="w-full text-left flex items-center px-4 py-3 text-sm text-[#5D3A1A] hover:bg-[#E0D5C7]/30 hover:text-[#C4943B] transition-colors duration-150 touch-manipulation"
                  role="menuitem"
                >
                  <CreditCardIcon className="w-5 h-5 mr-3" aria-hidden="true" />
                  Assinatura
                </button>
              </div>
              
              <div className="py-1 border-t border-[#E0D5C7]" role="none">
                <button
                  onClick={handleHelpCenter}
                  className="w-full text-left flex items-center px-4 py-3 text-sm text-[#5D3A1A] hover:bg-[#E0D5C7]/30 hover:text-[#C4943B] transition-colors duration-150 touch-manipulation"
                  role="menuitem"
                >
                  <QuestionMarkCircleIcon className="w-5 h-5 mr-3" aria-hidden="true" />
                  Central de Mensagens
                </button>
              </div>
              
              <div className="py-1 border-t border-[#E0D5C7]" role="none">
                <button
                  onClick={handleLogout}
                  className="w-full text-left flex items-center px-4 py-3 text-sm text-[#5D3A1A] hover:bg-[#E0D5C7]/30 hover:text-[#C4943B] transition-colors duration-150 touch-manipulation"
                  role="menuitem"
                >
                  <ArrowLeftStartOnRectangleIcon className="w-5 h-5 mr-3" aria-hidden="true" />
                  Sair
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Upgrade Button */}
        <Button
          onClick={() => setShowPricingModal(true)}
          variant="hero"
          size="default"
          className="flex items-center justify-center gap-1 md:gap-2 px-2 md:px-3 py-2 text-xs md:text-sm touch-manipulation"
          aria-label="Ver planos e fazer upgrade"
        >
          <ArrowTrendingUpIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span className="hidden sm:inline">Upgrade</span>
        </Button>

        {/* Dark/Light mode toggle - à direita de Upgrade, visível em desktop e mobile */}
        <ThemeToggle variant="header" />
      </div>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        onSelectPlan={() => {
          // Modal gerencia checkout internamente e redireciona para Stripe
          // Este callback pode ser usado para ações adicionais se necessário
        }}
      />
    </nav>
  );
};

export default Navbar;
